import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ActionLink, Input, Submit } from "@wine2trace/ui/FormComponents";
import useLabels from "../../hooks/useLabels";
import api from "../../api/axios";
import { activateAccount, confirmPasswordReset } from "../../services/utils";
import { PWD_REGEX } from "../../constants/regex";
import useLoginPageClass from "./useLoginPageClass";
import "./styles/index.scss";

// The link of the e-mail is `/reset-password#token=<token>` (or `/activate#token=<token>` in activation mode): the
// token travels in the URL fragment, which the browser does not send to any server nor puts in the Referer.
const readTokenFromHash = () => {
  const hash = window.location.hash.replace(/^#/, "");
  return new URLSearchParams(hash).get("token") || null;
};

// Same policy as the self-edit password form (UserForm validatePassword) and the server (clientsService PWD_REGEX):
// 6-20 characters, upper and lower case, a digit, one of #!@$%^&*()+=, no spaces. The first four are the login
// PWD_REGEX; the spaces are checked apart, as the server does.
const isValidPassword = (value) => PWD_REGEX.test(value) && !/\s/.test(value);

// Server codes meaning "this link cannot be used any more" (invalid, expired, already used, wrong account type).
const INVALID_LINK_CODES = ["passwordReset.003", "passwordReset.000"];
// Codes about the chosen password: shown under the form, the link is still good.
const PASSWORD_CODES = ["passwordReset.004", "passwordReset.005"];

// Answer of the activation endpoint when the link had expired: no password set, a new e-mail was sent.
const ACTIVATION_LINK_RENEWED = "email.005";

// Texts and service of the two flows sharing this page. "reset": password forgotten. "activation": first password
// of a new account (link of the activation e-mail).
const MODES = {
  reset: {
    submitService: confirmPasswordReset,
    title: "resetPasswordTitle",
    intro: null,
    submit: "resetPasswordSubmit",
    success: "resetPasswordSuccess",
    invalidTitle: "resetPasswordInvalidTitle",
    invalidText: "resetPasswordInvalidText",
  },
  activation: {
    submitService: activateAccount,
    title: "activationTitle",
    intro: "activationIntro",
    submit: "activationSubmit",
    success: "activationSuccess",
    invalidTitle: "activationInvalidTitle",
    invalidText: "activationInvalidText",
  },
};

const ResetPassword = ({ mode = "reset" }) => {
  const isActivation = mode === "activation";
  const texts = isActivation ? MODES.activation : MODES.reset;
  useLoginPageClass();
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  // Kept in state for the submit; read before the effect below removes it from the URL (also under StrictMode,
  // whose second effect run would find the fragment already gone).
  const [token] = useState(readTokenFromHash);
  const [invalid, setInvalid] = useState(!token);
  const [linkRenewed, setLinkRenewed] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const sending = useRef(false);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues: { password: "", confirm_password: "" }, mode: "onTouched" });

  useEffect(() => {
    // Drop the token from the address bar right away (history, screenshots, shoulder surfing): keep path and
    // query, and the router's own history state.
    const { pathname, search } = window.location;
    window.history.replaceState(window.history.state, "", `${pathname}${search}`);
  }, []);

  const onSubmit = async ({ password }) => {
    if (sending.current) return;
    sending.current = true;
    setSubmitError(null);
    try {
      const res = await texts.submitService(token, password, api);
      if (isActivation && res?.success === ACTIVATION_LINK_RENEWED) {
        // Expired link: nothing was set, a new activation e-mail is on its way.
        setLinkRenewed(true);
        return;
      }
      toast.success(getLabel(texts.success), { position: toast.POSITION.BOTTOM_RIGHT });
      navigate("/login", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.error;
      if (INVALID_LINK_CODES.includes(code)) {
        setInvalid(true);
      } else if (PASSWORD_CODES.includes(code)) {
        setSubmitError(getLabel(code));
      } else if (status === 429) {
        toast.error(getLabel("passwordResetRateLimited"), { position: toast.POSITION.BOTTOM_RIGHT });
      } else {
        toast.error(getLabel("passwordResetGenericError"), { position: toast.POSITION.BOTTOM_RIGHT });
      }
    } finally {
      sending.current = false;
    }
  };

  if (linkRenewed) {
    return (
      <div className="login login_wrapper red_card password_recovery">
        <h1>{getLabel("activationExpiredTitle")}</h1>
        <p className="password_recovery_notice" role="status">
          {getLabel("activationExpiredText")}
        </p>
        <div className="login_footer">
          <ActionLink to="/login">{getLabel("backToLogin")}</ActionLink>
        </div>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="login login_wrapper red_card password_recovery">
        <h1>{getLabel(texts.invalidTitle)}</h1>
        <p className="password_recovery_notice" role="alert">
          {getLabel(texts.invalidText)}
        </p>
        <div className="login_footer">
          {isActivation ? (
            <ActionLink to="/login">{getLabel("backToLogin")}</ActionLink>
          ) : (
            <ActionLink to="/forgot-password">{getLabel("resetPasswordRequestNew")}</ActionLink>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="login login_wrapper red_card password_recovery">
      <h1>{getLabel(texts.title)}</h1>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
        {texts.intro && <p className="password_recovery_text">{getLabel(texts.intro)}</p>}
        <p className="password_recovery_text">{getLabel("resetPasswordPolicyHint")}</p>
        <Input
          name="password"
          register={register}
          validate={(value) => isValidPassword(value) || getLabel("resetPasswordPolicyError")}
          type="password"
          label={getLabel("resetPasswordNew")}
          placeholder={getLabel("resetPasswordNewPlaceholder")}
          error={errors["password"]}
          required={getLabel("inputRequiredError")}
          isRequiredTooltip={false}
          onChange={() => setSubmitError(null)}
          showPasswordToggle={true}
        />
        <Input
          name="confirm_password"
          register={register}
          validate={(value) => value === getValues("password") || getLabel("resetPasswordMismatch")}
          type="password"
          label={getLabel("resetPasswordConfirm")}
          placeholder={getLabel("resetPasswordConfirmPlaceholder")}
          error={errors["confirm_password"]}
          required={getLabel("inputRequiredError")}
          isRequiredTooltip={false}
          onChange={() => setSubmitError(null)}
          showPasswordToggle={true}
        />
        <Submit customClassName="is_login" label={getLabel(texts.submit)} />
        {submitError && <div className="error_submit">{submitError}</div>}
      </form>
      <div className="login_footer">
        <ActionLink to="/login">{getLabel("backToLogin")}</ActionLink>
      </div>
    </div>
  );
};

export default ResetPassword;
